import CustomModal from "@components/common/CustomModal";
import { zodResolver } from "@hookform/resolvers/zod";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import HandshakeIcon from "@mui/icons-material/Handshake";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ReplyIcon from "@mui/icons-material/Reply";
import {
  Box,
  Button,
  DialogContent,
  Divider,
  Fade,
  IconButton,
  Paper,
  Popper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { useConfirm } from "material-ui-confirm";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as z from "zod";
import {
  useAcceptOfferMutation,
  useCreateOfferMutation,
  useDeleteOfferMutation,
  useRejectOfferMutation,
} from "@/libs/generated/hooks";
import type { CreateOfferInput, OfferStatus } from "@/libs/generated/types";
import { driverPausedErrorMessage } from "@/utils/driverPauseError";

const schema = z.object({
  demandedPrice: z.coerce.number().min(10),
});

export function ActionCell({
  deliveryId,
  id,
  driverId,
  status,
}: {
  deliveryId: number;
  id: number;
  driverId: number;
  status: OfferStatus;
}) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const moreRef = useRef(null);
  const navigate = useNavigate();
  const confirm = useConfirm();

  // Generated hooks
  const acceptOfferMutation = useAcceptOfferMutation({
    onSuccess: () => {
      toast("Offre acceptée avec succès", {
        type: "success",
        position: "bottom-right",
      });
      void navigate("/admin/pilot/delivery-table/set");
    },
    onError: (error: unknown) => {
      const errorMessage =
        driverPausedErrorMessage(error) ??
        (error instanceof Error ? error.message : "Une erreur est survenue");
      toast(errorMessage, {
        type: "error",
        position: "bottom-right",
      });
    },
  });
  const deleteOfferMutation = useDeleteOfferMutation();
  const rejectOfferMutation = useRejectOfferMutation({
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : "Une erreur est survenue";
      toast(errorMessage, {
        type: "error",
        position: "bottom-right",
      });
    },
  });

  const handleAcceptOffer = async (offerId: number) => {
    await acceptOfferMutation.mutateAsync({
      AcceptOfferInput: { id: offerId },
    });
    void queryClient.invalidateQueries({ queryKey: ["findAllOffers"] });
  };

  const handleRejectOfferClick = async () => {
    try {
      await rejectOfferMutation.mutateAsync({
        RejectOfferInput: { id },
      });
      void queryClient.invalidateQueries({ queryKey: ["findAllOffers"] });
      toast("Offre rejetée avec succès", {
        type: "success",
        position: "bottom-right",
      });
      // void queryClient.invalidateQueries({
      //   queryKey: ["Offers", { driver: true }, { deliveryId }],
      // });
      setOpen(false); // Close the dropdown menu
    } catch {
      toast("Une erreur est survenue", {
        type: "error",
        position: "bottom-right",
      });
    }
  };

  const handleDeleteOfferClick = async () => {
    try {
      await deleteOfferMutation.mutateAsync({
        DeleteInput: { id },
      });
      toast("Offre supprimée avec succès", {
        type: "success",
        position: "bottom-right",
      });
      void queryClient.invalidateQueries({ queryKey: ["findAllOffers"] });
      setOpen(false); // Close the dropdown menu
    } catch {
      toast("Une erreur est survenue", {
        type: "error",
        position: "bottom-right",
      });
    }
  };

  const handleAcceptOfferClick = () =>
    void confirm({
      title: <Typography>Souhaitez-vous accepter cette offre ?</Typography>,
    }).then(() => {
      void handleAcceptOffer(id);
    });
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", justifyContent: "center" }}>
      <Tooltip title="Clôturer le deal">
        <span>
          <IconButton
            aria-label="Clôturer le deal"
            disabled={status !== "PENDING"}
            onClick={handleAcceptOfferClick}
            color="primary"
            size="small"
          >
            <CheckCircleIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <ProposeOfferForm deliveryId={deliveryId} accepted={false} driverId={driverId} />
      <Tooltip title="Autres actions">
        <IconButton
          aria-label="Autres actions"
          size="small"
          color="primary"
          ref={moreRef}
          onClick={() => setOpen(!open)}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popper
        // Note: The following zIndex style is specifically for documentation purposes and may not be necessary in your application.
        sx={{ zIndex: 1200 }}
        open={open}
        anchorEl={moreRef.current}
        placement="right-start"
        transition
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper>
              <Typography p={1} variant="body2" textAlign="center">
                Autre actions
              </Typography>
              <Divider />
              <Stack p={2} direction="column" spacing={1}>
                <Button
                  fullWidth
                  variant="text"
                  color="error"
                  startIcon={<ReplyIcon fontSize="small" />}
                  onClick={() => {
                    void handleRejectOfferClick();
                  }}
                  size="small"
                >
                  Rejeter
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  color="error"
                  startIcon={<DeleteIcon fontSize="small" />}
                  onClick={() => {
                    void handleDeleteOfferClick();
                  }}
                  size="small"
                >
                  Supprimer
                </Button>
              </Stack>
            </Paper>
          </Fade>
        )}
      </Popper>
    </Stack>
  );
}

function ProposeOfferForm({
  deliveryId,
  accepted,
  driverId,
}: Omit<CreateOfferInput, "demandedPrice">) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      demandedPrice: 0,
    },
  });
  const queryClient = useQueryClient();
  const createOfferMutation = useCreateOfferMutation({
    onSuccess: () => {
      toast("Offre soumise avec succès", {
        type: "success",
        position: "bottom-right",
      });
      void queryClient.invalidateQueries({ queryKey: ["findAllOffers"] });
      reset();
    },
  });

  const sendOffer = async (data: { demandedPrice: number }) => {
    try {
      await createOfferMutation.mutateAsync({
        CreateOfferInput: {
          demandedPrice: Number(data.demandedPrice),
          deliveryId,
          accepted,
          driverId,
        },
      });
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          requestData: {
            demandedPrice: Number(data.demandedPrice),
            deliveryId,
            accepted,
            driverId,
          },
          error,
          component: "ProposeOfferForm",
        },
      });
      toast(driverPausedErrorMessage(error) ?? "Une erreur est survenue", {
        type: "error",
        position: "bottom-right",
      });
    }
  };

  const handleFormSubmit = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    void handleSubmit(sendOffer)();
  };

  return (
    <CustomModal
      btnSize="small"
      disabled={false}
      button={
        <Tooltip title="Proposer une offre">
          <IconButton aria-label="Proposer une offre" color="secondary" size="small">
            <HandshakeIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      }
      color="secondary"
      confirmText="Confirmer"
      title="Proposer un offre"
      formId="send-offer-form"
      // confirmAction={handleFormSubmit}
    >
      <DialogContent>
        <Box component="form" minWidth={400} onSubmit={handleFormSubmit} id="send-offer-form">
          <TextField
            {...register("demandedPrice")}
            label="Price" // optional
            fullWidth
            helperText={errors.demandedPrice?.message}
            error={Boolean(errors.demandedPrice)}
          />
        </Box>
      </DialogContent>
    </CustomModal>
  );
}
